import axios from "axios";
import {ChangeEvent, useContext, useRef, useState} from "react";
import { ControllerRenderProps, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useDebounceCallback } from "shared/lib/debounce/useDebounce";
import {useUserStore} from "entities/user";
import {$api} from "shared/api";
import {useHookFormMask} from "use-mask-input";
import {useModals} from "widgets/profile/lib/modalsStore.ts";
import {ConfigContext} from "../../../app/App.tsx";

type formValues = {
  login: string;
  city: string;
  email: string;
  firstName: string;
  lastName: string;
  patronymic: string;
  birthday: Date | undefined;
  
  previousPassword: string;
  password: string;
  repeatPassword: string;
}

const supportedExtensions = ['jpg', 'jpeg', 'png', 'webp'];
const getFileExtension = (fileName: string) => fileName.split(".")[fileName.split(".").length - 1];

export const useEditProfile = ({onClose}: { onClose?: () => void }) => {
  const setActiveSection = useModals((s) => s.change)
  
  const phoneMask = useRef('');
  
  const [ showPass, setShowPass ] = useState<boolean>(false);
  const [ showPrevPass, setShowPrevPass ] = useState<boolean>(false);
  const [ showConfirmPass, setShowConfirmPass ] = useState<boolean>(false);
  const [ isLoading, setIsLoading ] = useState<boolean>(false);
  const [ dataValues, setDataValues ] = useState<{name: string; value: string}[]>([])
  const [ showSuggestions, setShowSuggestions ] = useState<boolean>(false);
  
  const config = useContext(ConfigContext);
  
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  
  const userInfo = useUserStore((s) => s.userInfo);
  const updateUser = useUserStore((s) => s.updateUser);
  const updatePassword = useUserStore((s) => s.updatePassword);
  
  // eslint-disable-next-line
  const emailPattern = useRef<RegExp>(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,8})+$/)
  
  const {
    control,
    formState: { errors, isDirty },
    register,
    setValue,
    reset,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    trigger
  } = useForm<formValues>({
    defaultValues: {
      login: userInfo?.credentials.login || '',
      previousPassword: '',
      password: '',
      repeatPassword: '',
      city: userInfo?.address.city || '',
      email: userInfo?.credentials.email || '',
      firstName: userInfo?.name.first || '',
      lastName: userInfo?.name.last || '',
      patronymic: userInfo?.name.patronymic || '',
      birthday: userInfo?.profile.birthday ? new Date(userInfo.profile.birthday) : undefined,
    }
  })
  
  const registerWithMask = useHookFormMask(register);
  
  const changeFirstName = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'firstName'>,
  ) => {
    field.onChange(event);
  };
  
  const changeLastName = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'lastName'>,
  ) => {
    field.onChange(event);
  };
  
  const changePatronymic = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'patronymic'>,
  ) => {
    field.onChange(event);
  };
  
  const checkEmail = async () => {
    const email = getValues().email;
    const res = await $api.get(`/users?email=${email}`);
    
    if (res.data.total > 0) {
      setError('email', {message: 'Указанный e-mail уже используется'})
      return false;
    }
    
    clearErrors('email');
    return true;
  }
  
  const checkEmailDebounced = useDebounceCallback(checkEmail, 500)
  
  const changeEmail = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'email'>,
  ) => {
    if (emailPattern.current.test(getValues().email)) {
      checkEmailDebounced()
    }
    field.onChange(event);
  };
  
  const getCitySuggestions = useDebounceCallback(async () => {
    setDataValues([]);
    const { city } = getValues();
    if (!city?.trim?.()) {
      setShowSuggestions(false);
      return;
    }

    setShowSuggestions(true);
    const cityRes = await axios.post('https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address', {query: city, from_bound: {value: 'city'}}, {
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "Authorization": "Token " + config.VITE_DADATA_TOKEN
      }
    }).catch(() => {setShowSuggestions(false)})
    
    if (cityRes?.data?.suggestions?.length) {
      const options = cityRes.data.suggestions.map((el: any)=>{
        return {
          name: el.value,
          value: el.data.city
        }
      })
      setDataValues(options);
    } else {
      setShowSuggestions(false);
    }
  }, 500)
  
  const changeCity = async (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'city'>,
  ) => {
    field.onChange(event);
    getCitySuggestions();
  };
  
  const onSuggestionClick = (value: string) => {
    setShowSuggestions(false);
    setValue('city', value);
    setDataValues([])
  }
  
  const changePrevPass = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'previousPassword'>,
  ) => {
    field.onChange(event);
  };
  
  const changePass = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'password'>,
  ) => {
    field.onChange(event);
  };
  
  const changeConfirmPass = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'repeatPassword'>,
  ) => {
    field.onChange(event);
  };
  
  const changeBirthDay = (
    date: Date | undefined,
    field: ControllerRenderProps<formValues, 'birthday'>,
  ) => {
    field.onChange(date);
  }
  
  const onPersDataSubmit = async (data: formValues) => {
    if (!isDirty) return
    
    setIsLoading(true);
    const requestBody = {
      attachmentId: userInfo?.profile.avatar?.id,
      firstName: data.firstName,
      lastName: data.lastName,
      patronymic: data.patronymic,
      email: data.email,
      birthday: data.birthday || new Date(),
      city: data.city,
      phone: data.login,
      code: '0000'
    }
    
    try {
      const res = await updateUser(requestBody)
      
      if (res?.status === 200||res?.status === 201) {
        toast.success('Данные успешно отредактированы');
        setActiveSection(null)
        if (onClose) onClose()
      } else {
        toast.error('Произошла ошибка при редактированни');
      }
    } catch (e) {
      console.warn(e)
    } finally {
      setIsLoading(false);
    }
  }
  
  const getAttachment = async (files: FileList, index: number) => {
    const file = files[index];
    if (!file.type.includes('image')) {
      if (files[index+1]) {
        return getAttachment(files, index+1)
      }
      return
    }
    
    const fd = new FormData();
    fd.set('file',file);
    const res = await $api.post('/attachments/files/upload', fd);
    
    const { id } = res.data;
    
    const requestBody = {
      attachmentId: id as string,
      firstName: userInfo?.name.first,
      lastName: userInfo?.name.last,
      patronymic: userInfo?.name.patronymic,
      email: userInfo?.credentials.email,
      birthday: new Date(userInfo?.profile.birthday || ''),
      city: userInfo?.address.city,
      phone: userInfo?.credentials.login,
    }
    
    setIsLoading(true);
    
    try {
      const res = await updateUser(requestBody).catch(() => {
        setIsLoading(false);
      });
      
      if (res?.status === 200||res?.status === 201) {
        toast.success('Фотография успешно обновлена');
      } else {
        toast.error('Произошла ошибка при обновлении');
      }
    } catch (e) {
      console.warn(e)
      setIsLoading(false);
    }
  }
  
  const onFileChange = async () => {
    if (!fileInputRef.current) return;
    
    const files = fileInputRef.current.files;
    if (!files || !files.length) return;
    
    const file = files[0]
    
    if (file) {
      if (file.size > 1000000) {
        toast.error('Размер файла превышает 1МБ');
        return
      }
      
      const fileExt = getFileExtension(file.name);
      if (!supportedExtensions.includes(fileExt)) {
        toast.error('Неподдерживаемое расширение файла');
        return
      }
    }
    
    await getAttachment(files, 0);
  }
  
  const onPassUpdate = async (data: formValues) => {
    if (data.password !== data.repeatPassword) {
      setError('password', {message: 'Пароли не совпадают'})
      setError('repeatPassword', {message: 'Пароли не совпадают'})
      return
    }
    
    const body = {
      new: data.password.trim(),
      current: data.previousPassword.trim(),
    }
    
    setIsLoading(true);
    
    try {
      const res = await updatePassword(body).catch((e) => {
        if (e?.status === 403) {
          setError('previousPassword', {message: 'Неверный пароль'})
        } else {
          toast.error('Произошла ошибка при редактировании');
        }
      })
      
      if (res?.status === 200||res?.status === 201) {
        toast.success('Данные успешно отредактированы');
        if (onClose) onClose()
      }
    } catch (e) {
      console.warn(e)
      toast.error('Произошла ошибка при редактировании');
    } finally {
      setIsLoading(false);
    }
    
  }
  
  const prevPassValidate = () => {
    const { password, previousPassword } = getValues();
    
    if (password === previousPassword) {
      return 'Новый пароль не должен совпадать с текущим'
    }
    
    clearErrors('previousPassword');
    return true
  }
  
  const passValidate = () => {
    const { password, previousPassword, repeatPassword } = getValues();
    
    if (password === previousPassword) {
      return 'Новый пароль не должен совпадать с текущим'
    }
    if (password !== repeatPassword) {
      return 'Пароли не совпадают'
    }
    
    clearErrors('password');
    return true
  }
  
  const repeatPassValidate = () => {
    const { password, repeatPassword } = getValues();
    
    if (password !== repeatPassword) {
      return 'Пароли не совпадают'
    }
    
    clearErrors('repeatPassword');
    return true
  }
  
  return {
    control,
    phoneMask,
    errors,
    showPass,
    showPrevPass,
    showConfirmPass,
    isLoading,
    dataValues,
    showSuggestions,
    emailPattern,
    registerWithMask,
    handleSubmit,
    setValue,
    isDirty,
    changeFirstName,
    changeLastName,
    changePatronymic,
    changeEmail,
    changeCity,
    onPersDataSubmit,
    changeBirthDay,
    onPassUpdate,
    setShowPass,
    setShowPrevPass,
    setShowConfirmPass,
    changePrevPass,
    changePass,
    changeConfirmPass,
    clearErrors,
    getValues,
    trigger,
    prevPassValidate,
    passValidate,
    repeatPassValidate,
    onSuggestionClick,
    fileInputRef,
    onFileChange,
    reset
  }
}